import { z } from 'zod';

import { dashboardT } from '../../../lib/messages';
import type { AppRequest, CreateAppFromRequestPayload } from './model';

export const appRequestEditSchema = z.object({
  appId: z.string(),
  blocked: z.boolean(),
  note: z.string(),
});

const required = z
  .string()
  .trim()
  .min(1, { error: () => dashboardT('api.common.validationFailed') });

export const createAppFromRequestSchema = z.object({
  name: required,
  appStoreUrl: required,
  appStoreId: required,
  platformId: required,
  bundleId: required,
  domain: z.string(),
  adsTxtLocation: z.string(),
  iabCategoryIds: z.array(z.string()).max(1),
  description: z.string(),
  blocked: z.boolean(),
  note: z.string(),
});

export type AppRequestEditValue = z.infer<typeof appRequestEditSchema>;
export type CreateAppFromRequestFormValue = z.infer<typeof createAppFromRequestSchema>;

interface AppRequestEditDirtyFields {
  appId?: boolean;
  blocked?: boolean;
  note?: boolean;
}

export function appRequestInitialAppId(requests: AppRequest[]): string {
  const appId = requests[0]?.appId ?? requests[0]?.suggestedAppId ?? '';
  return requests.every((request) => (request.appId ?? request.suggestedAppId ?? '') === appId)
    ? appId
    : '';
}

export function applyAppRequestEdit(
  requests: AppRequest[],
  value: AppRequestEditValue,
  dirtyFields: AppRequestEditDirtyFields,
) {
  const applyEveryField = requests.length === 1;
  const applyApp =
    applyEveryField || dirtyFields.appId || Boolean(appRequestInitialAppId(requests));
  return requests.map((request) => {
    const appId = request.appId ?? (applyApp ? value.appId || null : null);
    const blocked = applyEveryField || dirtyFields.blocked ? value.blocked : request.blocked;
    const note = applyEveryField || dirtyFields.note ? value.note : request.note;
    return {
      ...request,
      appId,
      blocked,
      note,
      processed: Boolean(appId) || blocked || request.processed,
    };
  });
}

export function createAppFromRequestInitialValue(
  request: AppRequest,
): CreateAppFromRequestFormValue {
  return {
    name: request.suggestedAppName ?? request.requestAppName ?? request.bundleId ?? '',
    appStoreUrl: request.appStoreUrl ?? '',
    appStoreId: request.suggestedAppStoreId ?? '',
    platformId: request.platformId ?? '',
    bundleId: request.bundleId ?? '',
    domain: '',
    adsTxtLocation: '',
    iabCategoryIds: [],
    description: '',
    blocked: request.blocked,
    note: request.note ?? '',
  };
}

export function serializeCreateAppFromRequest(
  value: CreateAppFromRequestFormValue,
): CreateAppFromRequestPayload {
  return {
    name: value.name.trim(),
    appStoreUrl: value.appStoreUrl.trim(),
    appStoreId: value.appStoreId.trim(),
    platform: value.platformId,
    bundleId: value.bundleId.trim(),
    bundleUrl: null,
    appId: null,
    domain: value.domain.trim(),
    adsTxtLocation: value.adsTxtLocation.trim(),
    iabCategoriesIds: value.iabCategoryIds,
    description: value.description.trim(),
  };
}
